import { chargeKeys } from '@/hooks/use-charges';
import { getMarketingHomeEnrollments } from '@/content/marketing/home-mock-data';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { isMarketingMockEnabled } from '@/lib/marketing-mock/is-marketing-mock-enabled';
import { getMarketingMockQueryOptions } from '@/lib/marketing-mock/query-options';
import api from '@/services/api';
import { CreateEnrollmentDto, Enrollments } from '@/types/enrollments';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { USER_ROLE } from '@/types/user-role';
import { isProviderRole } from '@/utils/user-role';

// Query keys
export const enrollmentKeys = {
  all: ['enrollments'] as const,
  lists: () => [...enrollmentKeys.all, 'list'] as const,
  list: (filters?: { q?: string; isActive?: boolean }) =>
    [...enrollmentKeys.lists(), filters] as const,
  details: () => [...enrollmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...enrollmentKeys.details(), id] as const,
  provider: () => [...enrollmentKeys.all, 'provider'] as const,
  client: () => [...enrollmentKeys.all, 'client'] as const,
};

type PaginatedResponse<T> = {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

// Provider enrollments
export function useProviderEnrollments(filters?: { page?: number; limit?: number }) {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isProvider = isProviderRole(user?.role);

  return useQuery({
    queryKey: [...enrollmentKeys.provider(), filters],
    queryFn: async () => {
      const params: Record<string, any> = {};
      // Sempre envia page e limit para garantir formato paginado
      params.page = filters?.page || 1;
      params.limit = filters?.limit || 10;

      const response = await api.get<PaginatedResponse<Enrollments>>('/provider/enrollments', {
        params,
      });

      // Garantir que sempre retorna formato paginado
      const data = response.data;
      if (Array.isArray(data)) {
        // Se vier como array (formato antigo), converter para formato paginado
        return {
          data,
          meta: {
            page: 1,
            limit: data.length,
            total: data.length,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        };
      }

      return data;
    },
    // Só providers (PROVIDER/INDIVIDUAL/MASTER/SUB_PROVIDER) devem chamar /provider/enrollments
    enabled: isHydrated && isProvider,
    ...getMarketingMockQueryOptions(
      isMarketingMockEnabled() ? getMarketingHomeEnrollments(filters) : undefined,
    ),
  });
}

// Client enrollments
export function useClientEnrollments(filters?: { q?: string; isActive?: boolean }) {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isClient = user?.role === USER_ROLE.CLIENT;

  return useQuery({
    queryKey: enrollmentKeys.list(filters),
    queryFn: async () => {
      const params: Record<string, any> = {};
      if (filters?.q) params.q = filters.q;
      if (filters?.isActive !== undefined) params.isActive = filters.isActive;

      const response = await api.get<Enrollments[]>('/client/enrollments', {
        params,
      });
      return response.data;
    },
    // Somente clientes devem chamar /client/enrollments
    enabled: isHydrated && isClient,
  });
}

// Single enrollment
export function useEnrollment(id: string | undefined) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: enrollmentKeys.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Enrollment ID is required');
      const response = await api.get<Enrollments>(`/enrollments/${id}`);
      return response.data;
    },
    enabled: isHydrated && !!id,
  });
}

// Create enrollment mutation
export function useCreateEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateEnrollmentDto) => {
      const response = await api.post<Enrollments>('/enrollments', data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.provider() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.client() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
    },
  });
}

// Update enrollment mutation
export function useUpdateEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateEnrollmentDto> }) => {
      const response = await api.patch<Enrollments>(`/enrollments/${id}`, data);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.provider() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.client() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
    },
  });
}

// Delete enrollment mutation (removes contract, charges and service schedule)
export function useDeleteEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<Enrollments>(`/enrollments/${id}`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.provider() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.client() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: chargeKeys.byEnrollment(id) });
    },
  });
}
